import React, { useState } from "react";
import { PROMPT_VARIABLES, VARIABLE_CATEGORIES } from "./PromptVariables";
import { CATEGORY_COLORS } from "./constants";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

// --- Import New Modals ---
import CoachNameConfigModal from "./CoachNameConfigModal";
import PersonalityConfigModal from "./PersonalityConfigModal";
import PaywallConfigModal from "./PaywallConfigModal";
// NOTE: Imports are now active.

// --- VariableItem Component ---
const VariableItem = ({
  variableKey,
  variable,
  onInsert,
  currentProtocol,
  handleProtocolChange,
}) => {
  const colors = CATEGORY_COLORS[variable.category];
  const Icon = variable.icon;

  const [isCoachNameModalOpen, setIsCoachNameModalOpen] = useState(false);
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);

  const handleSettingsClick = (e) => {
    e.stopPropagation();
    if (!variable.enabled) return;

    if (variableKey === "coach_name") {
      setIsCoachNameModalOpen(true);
    } else if (variableKey === "personality") {
      setIsPersonalityModalOpen(true);
    } else if (variableKey === "paywall") {
      setIsPaywallModalOpen(true);
    }
  };

  const isSpecialEditable = ["coach_name", "personality", "paywall"].includes(
    variableKey
  );

  return (
    <>
      <div
        className={`group flex items-center gap-3 p-3 rounded-lg transition-colors duration-150 ease-in-out ${
          variable.enabled ? colors.hover : "opacity-50"
        } ${variable.enabled ? "cursor-pointer" : "cursor-not-allowed"}`}
        onClick={variable.enabled ? () => onInsert(variableKey) : undefined}
        title={
          variable.enabled
            ? `Insert {${variableKey}}`
            : `${variable.name} (Disabled)`
        }
      >
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}
        >
          <Icon className={`h-5 w-5 ${colors.text}`} />
        </div>
        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 flex items-center">
            {variable.name}
            {!variable.enabled && (
              <span className="ml-2 text-xs text-gray-400">(Disabled)</span>
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {variable.description}
          </div>
        </div>
        {/* Settings Icon */}
        {isSpecialEditable && variable.enabled && (
          <button
            onClick={handleSettingsClick}
            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-opacity focus:opacity-100"
            aria-label={`Configure ${variable.name}`}
            title={`Configure ${variable.name}`}
          >
            <Cog6ToothIcon className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Render new modals conditionally */}
      {isCoachNameModalOpen && (
        <CoachNameConfigModal
          protocol={currentProtocol}
          handleProtocolChange={handleProtocolChange}
          onClose={() => setIsCoachNameModalOpen(false)}
        />
      )}
      {isPersonalityModalOpen && (
        <PersonalityConfigModal
          protocol={currentProtocol}
          handleProtocolChange={handleProtocolChange}
          onClose={() => setIsPersonalityModalOpen(false)}
        />
      )}
      {isPaywallModalOpen && (
        <PaywallConfigModal
          protocol={currentProtocol}
          handleProtocolChange={handleProtocolChange}
          onClose={() => setIsPaywallModalOpen(false)}
        />
      )}
    </>
  );
};

// --- CategorySection Component ---
const CategorySection = ({
  category,
  variables,
  onInsert,
  currentProtocol,
  handleProtocolChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const colors = CATEGORY_COLORS[category];

  const categoryVariables = Object.entries(variables).filter(
    ([_, v]) => v.category === category
  );

  if (categoryVariables.length === 0) return null;

  return (
    <div className="mb-4">
      {/* Category Header */}
      <div className="sticky top-0 px-4 py-2.5 bg-white/90 backdrop-blur-sm border-b z-10">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`${colors.text} hover:opacity-75`}
            aria-expanded={isExpanded}
            aria-controls={`category-content-${category.replace(/\s+/g, "-")}`}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
            <span className="sr-only">
              {isExpanded ? "Collapse" : "Expand"} {category}
            </span>
          </button>
          <h3
            className={`text-xs font-semibold mb-0 uppercase tracking-wider ${colors.text}`}
            id={`category-title-${category.replace(/\s+/g, "-")}`}
          >
            {category}
            <span className="ml-1 text-gray-400 font-normal">
              ({categoryVariables.length})
            </span>
          </h3>
        </div>
      </div>

      {/* Category Content */}
      {isExpanded && (
        <div
          className="space-y-1 mt-1 px-2"
          id={`category-content-${category.replace(/\s+/g, "-")}`}
          role="region"
          aria-labelledby={`category-title-${category.replace(/\s+/g, "-")}`}
        >
          {categoryVariables.map(([key, variable]) => (
            <VariableItem
              key={key}
              variableKey={key}
              variable={variable}
              onInsert={onInsert}
              currentProtocol={currentProtocol}
              handleProtocolChange={handleProtocolChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- Main Sidebar Component ---
export default function PromptVariableSidebar({
  className,
  onInsert,
  currentProtocol,
  handleProtocolChange,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredVariables = searchQuery
    ? Object.fromEntries(
        Object.entries(PROMPT_VARIABLES).filter(
          ([key, variable]) =>
            variable.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            variable.description
              ?.toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            key.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : PROMPT_VARIABLES;

  return (
    <div
      className={`w-80 bg-white border-r border-gray-200 flex flex-col ${
        className || ""
      }`}
    >
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-20">
        <div className="relative">
          <label htmlFor="variable-search" className="sr-only">
            Search variables
          </label>
          <input
            id="variable-search"
            type="text"
            placeholder="Search variables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-50 text-gray-900 placeholder-gray-400 rounded-lg pl-10 pr-4 py-2 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Variable List */}
      <div className="flex-1 overflow-y-auto pt-2">
        {VARIABLE_CATEGORIES.map((category) =>
          currentProtocol ? (
            <CategorySection
              key={category}
              category={category}
              variables={filteredVariables}
              onInsert={onInsert}
              currentProtocol={currentProtocol}
              handleProtocolChange={handleProtocolChange}
            />
          ) : null
        )}
        {!currentProtocol && (
          <div className="p-4 text-center text-gray-500">
            Loading variables...
          </div>
        )}
      </div>
    </div>
  );
}
